import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, timeout} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ClientPreparationResult} from '../../models/client-production-config-sync.model';
import {ClientProductionConfigSyncService} from './client-production-config-sync.service';

export interface ClientPreparationState {
    clientId: string;
    running: boolean;
    result?: ClientPreparationResult;
    error?: string;
}

export function isLocalClientPreparation(hostname: string, name: string, production: boolean): boolean {
    return !production && ['localhost', '127.0.0.1', '[::1]', '::1'].includes(hostname.toLowerCase()) &&
        ['local', 'development'].includes(name.toLowerCase());
}

@Injectable({providedIn: 'root'})
export class ClientPreparationService {
    private readonly api = inject(ClientProductionConfigSyncService);
    private readonly pending = new Map<string, Promise<void>>();
    private readonly completed = new Map<string, ClientPreparationState>();
    private readonly revisions = new Map<string, number>();
    private selectedClientId = '';
    private epoch = 0;
    readonly state = signal<ClientPreparationState | null>(null);

    get available(): boolean {
        return environment.localClientPreparationEnabled && environment.authenticationProvider !== 'auth0' &&
            isLocalClientPreparation(location.hostname, environment.environment, environment.production);
    }

    select(clientId: string, hasAi: boolean): void {
        const changed = this.selectedClientId !== clientId;
        this.selectedClientId = clientId;
        if (!this.available || !hasAi) {
            this.selectedClientId = '';
            this.state.set(null);
            return;
        }
        const previous = this.completed.get(clientId);
        if (previous && !changed) this.state.set(previous);
        else this.start(clientId);
    }

    retry(): void {
        if (this.available && this.selectedClientId) this.start(this.selectedClientId);
    }

    async prepareForImport(clientId: string): Promise<void> {
        if (!this.available) return;
        const epoch = this.epoch;
        const running = this.pending.get(clientId);
        if (running) await running;
        if (epoch !== this.epoch || (this.selectedClientId && this.selectedClientId !== clientId)) return;
        this.start(clientId);
        await this.pending.get(clientId);
    }

    acceptImportResult(result: ClientPreparationResult): void {
        if (!this.available) return;
        this.revisions.set(result.clientId, (this.revisions.get(result.clientId) ?? 0) + 1);
        const state = {clientId: result.clientId, running: false, result};
        this.completed.set(result.clientId, state);
        if (this.selectedClientId === result.clientId) this.state.set(state);
    }

    reset(): void {
        this.epoch++;
        this.selectedClientId = '';
        this.completed.clear();
        this.pending.clear();
        this.revisions.clear();
        this.state.set(null);
    }

    private start(clientId: string): void {
        if (this.selectedClientId === clientId) this.state.set({clientId, running: true});
        if (this.pending.has(clientId)) return;
        const epoch = this.epoch;
        const revision = (this.revisions.get(clientId) ?? 0) + 1;
        this.revisions.set(clientId, revision);
        const work = this.prepare(clientId, epoch, revision);
        this.pending.set(clientId, work);
        void work.finally(() => {
            if (epoch === this.epoch) this.pending.delete(clientId);
        });
    }

    private async prepare(clientId: string, epoch: number, revision: number): Promise<void> {
        let state: ClientPreparationState;
        try {
            const result = await firstValueFrom(this.api.prepareLocal(clientId).pipe(timeout(330_000)));
            if (result.clientId !== clientId) throw new Error('Unexpected preparation response.');
            state = {clientId, running: false, result};
        } catch (error: unknown) {
            const response = error as {error?: {error?: unknown}};
            const message = response.error?.error;
            state = {clientId, running: false, error: typeof message === 'string' && message.trim()
                ? message : 'Local preparation was not confirmed complete. Check the local services and try again.'};
        }
        if (epoch !== this.epoch || revision !== this.revisions.get(clientId)) return;
        this.completed.set(clientId, state);
        if (this.selectedClientId === clientId) this.state.set(state);
    }
}
